// DAS-2765: reusable market weather badge (CALM / NERVOUS / PANIC).
// Fetches /market_weather once and renders a small colored pill; the tooltip
// explains exactly how the flag is calculated. Renders nothing while no data
// exists, so it can be mounted anywhere without layout risk.
import React, { useState, useEffect } from 'react';
import { Tooltip } from 'antd';
import api from '../functions/api';

const COLORS = {
  CALM: { bg: '#f6ffed', border: '#b7eb8f', text: '#389e0d' },
  NERVOUS: { bg: '#fff7e6', border: '#ffd591', text: '#d46b08' },
  PANIC: { bg: '#fff1f0', border: '#ffa39e', text: '#cf1322' },
};

// Shared with the terminal statusbar badge (TerminalLayout.jsx) as plain text.
// Mirrors the thresholds in pythonscripts/DC_Market_Weather.py THRESHOLDS.
export const weatherExplanation = (w) => {
  const dd = w.drawdown_pct;      // negative percent
  const wild = w.wildness_pct;    // percent
  const vix = w.vix_close;        // points, may be null
  const ddVote = dd <= -10 ? 'PANIC vote (≤ −10%)'
    : dd <= -4 ? 'NERVOUS vote (≤ −4%)'
    : 'calm (above −4%)';
  const wildVote = (wild >= 30 && dd <= -7) ? 'PANIC vote (≥ 30 while ≥ 7% under the top)'
    : wild >= 20 ? 'NERVOUS vote (≥ 20)'
    : 'calm (below 20)';
  const vixVote = vix == null ? 'no value for this date'
    : vix >= 40 ? 'PANIC vote (≥ 40)'
    : vix >= 25 ? 'NERVOUS vote (≥ 25)'
    : 'calm (below 25)';
  return [
    `Market weather per ${w.date}: ${w.weather}`,
    '',
    'Calculated daily from the S&P 500 and the VIX (the market\'s "fear index"):',
    `1. Drawdown — today's close vs the highest close of the past year: ${dd}%`,
    `2. Wildness — size of the daily swings over the last 10 trading days (annualized): ${wild}%`,
    `3. VIX — expected swings priced by the options market (reacts fastest in a crash): ${vix != null ? vix : '—'}`,
    '',
    "Today's computation (the strongest vote wins):",
    `• Drawdown ${dd}% → ${ddVote}`,
    `• Wildness ${wild}% → ${wildVote}`,
    `• VIX ${vix != null ? vix : '—'} → ${vixVote}`,
    `→ Result: ${w.weather}`,
  ].join('\n');
};

// DAS-2801: antd tooltips sit at z-index 1070 and the MUI Dialog at 1300, so inside
// the Broad Picture full-view modal this badge's explanation rendered BEHIND the
// dialog and could not be read. Lift it above; harmless where there is no modal.
const TIP_Z = 2000;

const MarketWeatherBadge = ({ style = {} }) => {
  const [weather, setWeather] = useState(null);

  useEffect(() => {
    api.get('/market_weather')
      .then(res => setWeather(res.data?.current || null))
      .catch(() => setWeather(null));
  }, []);

  if (!weather) return null;
  const c = COLORS[weather.weather] || COLORS.CALM;

  return (
    <Tooltip
      zIndex={TIP_Z}
      overlayStyle={{ maxWidth: 380 }}
      title={<span style={{ whiteSpace: 'pre-line', fontSize: 12 }}>{weatherExplanation(weather)}</span>}
    >
      <span
        data-testid="market-weather-badge"
        style={{
          display: 'inline-block',
          padding: '1px 10px',
          borderRadius: 12,
          fontSize: 12,
          fontWeight: 600,
          background: c.bg,
          border: `1px solid ${c.border}`,
          color: c.text,
          cursor: 'default',
          whiteSpace: 'nowrap',
          ...style,
        }}
      >
        market weather: {weather.weather}
      </span>
    </Tooltip>
  );
};

export default MarketWeatherBadge;
